import React, { useState, useEffect } from 'react';
import { Chess } from 'chess.js';
import Board from '../../components/ArchivedChess/Board';
import Stockfish from '../Stockfish';
import ChessAnalysisWrapper from '../../util/ChessAnalysisWrapper';
import { useChessAnalysis } from '../../util/ChessAnalysisContext';
import stockfishEngine from '../../util/StockfishEngine'; // Import the engine directly for testing

// Test component to display current analysis state
const AnalysisStatus = () => {
  const { 
    currentPosition, 
    isAnalyzing, 
    analysisResults, 
    engineReady 
  } = useChessAnalysis();

  const [engineTestResult, setEngineTestResult] = useState(null);

  // Function to test if the engine loads properly
  const testEngineLoading = async () => {
    setEngineTestResult("Testing engine loading...");
    try {
      // Try to create a Web Worker
      const workerPaths = [
        '/stockfish-nnue-16-single.js',
        '/stockfish.js',
        '/stockfish.worker.js'
      ];
      
      let worker = null;
      let successPath = null;
      
      // Try each worker path
      for (const path of workerPaths) {
        try {
          setEngineTestResult(prev => prev + `\nTrying worker at path: ${path}`);
          worker = new Worker(path);
          successPath = path;
          break;
        } catch (error) {
          setEngineTestResult(prev => prev + `\nFailed to load worker from ${path}: ${error.message}`);
        }
      }
      
      if (!worker) {
        throw new Error("Could not initialize worker from any path");
      }
      
      setEngineTestResult(prev => prev + `\nWorker successfully created from ${successPath}`);
      
      // Set up message handling
      let receivedMessage = false;
      
      const messagePromise = new Promise((resolve, reject) => {
        // Set a timeout
        const timeout = setTimeout(() => {
          reject(new Error("Timed out waiting for worker response"));
        }, 5000);
        
        worker.onmessage = (event) => {
          clearTimeout(timeout);
          receivedMessage = true;
          const message = event.data;
          setEngineTestResult(prev => prev + `\nReceived message from worker: ${JSON.stringify(message).substring(0, 100)}`);
          resolve(message);
        };
        
        worker.onerror = (error) => {
          clearTimeout(timeout);
          setEngineTestResult(prev => prev + `\nWorker error: ${error.message}`);
          reject(error);
        };
        
        // Send a command to the engine
        setEngineTestResult(prev => prev + "\nSending 'uci' command to worker");
        worker.postMessage('uci');
      });
      
      await messagePromise;
      
      setEngineTestResult(prev => prev + "\nEngine test completed successfully!");
      
      // Clean up the worker
      worker.terminate();
      
    } catch (error) {
      setEngineTestResult(prev => prev + `\nTest failed: ${error.message}`);
    }
  };

  return (
    <div style={{ padding: '1rem', backgroundColor: '#f3f3f3', borderRadius: '4px', margin: '1rem 0' }}>
      <h3>Analysis Status</h3>
      <p><strong>Engine Ready:</strong> {engineReady ? 'Yes' : 'No'}</p>
      <p><strong>Currently Analyzing:</strong> {isAnalyzing ? 'Yes' : 'No'}</p>
      <p><strong>Current Position:</strong> <code>{currentPosition || 'Not set'}</code></p>
      <p><strong>Analysis Results:</strong> {analysisResults ? 'Available' : 'None'}</p>
      <pre style={{ maxHeight: '100px', overflow: 'auto', fontSize: '0.8rem' }}>
        {analysisResults ? JSON.stringify(analysisResults, null, 2) : 'No results yet'}
      </pre>
      
      <div style={{ marginTop: '1rem' }}>
        <button 
          onClick={testEngineLoading}
          style={{
            padding: '8px 16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test Engine Loading
        </button>
        
        {engineTestResult && (
          <pre style={{ 
            maxHeight: '200px', 
            overflow: 'auto', 
            marginTop: '1rem', 
            padding: '0.5rem', 
            backgroundColor: '#f8f8f8', 
            borderRadius: '4px',
            whiteSpace: 'pre-wrap'
          }}>
            {engineTestResult}
          </pre>
        )}
      </div>
    </div>
  );
};

// Main test component
const ChessAnalysisTest = () => {
  const [game] = useState(new Chess());
  const [moves, setMoves] = useState([]);
  const [currentMove, setCurrentMove] = useState('-1');
  const [boardSize, setBoardSize] = useState(600);

  // Set up a window handler for getCurrentBoardPosition for legacy code
  useEffect(() => {
    window.getCurrentBoardPosition = () => {
      return game.fen();
    };
    
    return () => {
      delete window.getCurrentBoardPosition;
    };
  }, [game]);

  return (
    <ChessAnalysisWrapper>
      <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
        <h1>Chess Analysis Test</h1>
        <p>This page demonstrates the communication between Board.jsx and Stockfish.js using the context API.</p>
        
        <div style={{ display: 'flex', flexDirection: 'row', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 600px' }}>
            <h2>Board</h2>
            <Board
              game={game}
              orientation="white"
              moves={moves}
              setMoves={setMoves}
              currentMove={currentMove}
              setCurrentMove={setCurrentMove}
              boardSize={boardSize}
              boardTheme="blue"
              showBoardNotation={true}
            />
          </div>
          
          <div style={{ flex: '1 1 500px' }}>
            <h2>Analysis</h2>
            <AnalysisStatus />
            <Stockfish isViewGame={true} />
          </div>
        </div>
      </div>
    </ChessAnalysisWrapper>
  );
};

export default ChessAnalysisTest; 